import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CompanyHeader from '@/components/company/CompanyHeader';
import MainTable, { MainTableColumn } from '@/components/company/table/MainTable';
import SearchBar from '@/components/company/filters/SearchBar';
import FilterSelect from '@/components/company/filters/FilterSelect';
import ClearFilterButton from '@/components/company/filters/ClearFilterButton';
import useTenantState from '@/states/tenant.state';
import useKpiTemplateState from '@/states/kpiTemplate.state';
import { useGetOrgChartNames } from './sop/useSopMedia';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import { useIsEmployee, useIsReadOnly } from '@/utils/auth/roleChecker';
import { IKpiTemplate } from '@/types/kpi-template.types';

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const STATUS_OPTIONS = [
  { label: 'Aktif', value: 'ACTIVE' },
  { label: 'Tidak Aktif', value: 'DRAFT' },
];

const MONTHS_ID = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
];

function formatPeriodLabel(period?: string): string {
  if (!period) return '-';
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (!match) return period;

  const year = match[1];
  const monthIndex = Number(match[2]) - 1;
  if (monthIndex < 0 || monthIndex > 11) return period;

  return `${MONTHS_ID[monthIndex]} ${year}`;
}

function StatusBadge({ status }: { status?: string }) {
  const map: Record<string, string> = {
    ACTIVE: 'bg-green-100 text-green-700',
    INACTIVE: 'bg-gray-100 text-gray-600',
    DRAFT: 'bg-gray-100 text-gray-600',
  };
  const label: Record<string, string> = { ACTIVE: 'Aktif', INACTIVE: 'Tidak Aktif', DRAFT: 'Tidak Aktif' };
  const cls = map[status ?? ''] ?? 'bg-gray-100 text-gray-600';
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {label[status ?? ''] ?? status ?? '-'}
    </span>
  );
}

export default function KpiTemplate() {
  const { tenant_id } = useParams();
  const navigate = useNavigate();
  const isEmployee = useIsEmployee();
  const isReadOnly = useIsReadOnly() && !isEmployee;
  const { currentTenant } = useTenantState();
  const { paginatedData, isLoading, getTemplates, deleteTemplate } = useKpiTemplateState();
  const { data: orgChartNames = [] } = useGetOrgChartNames(currentTenant?.id);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [startMonth, setStartMonth] = useState('');
  const [endMonth, setEndMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'created_at',
    order: 'desc',
  });

  const fetchData = useCallback(async () => {
    if (!currentTenant?.id) return;

    let periodParam = undefined;
    if (startMonth || endMonth || selectedYear) {
      const currentYear = new Date().getFullYear().toString();

      const monthStartLabel = startMonth ? MONTHS_ID[Number(startMonth) - 1] : '%';
      const monthEndLabel = endMonth ? MONTHS_ID[Number(endMonth) - 1] : '%';
      const yearStr = selectedYear || currentYear;

      periodParam = `${monthStartLabel} - ${monthEndLabel} ${yearStr}`;
    }

    await getTemplates(currentTenant.id, {
      page: pageConfig.page + 1,
      page_size: pageConfig.limit,
      order: pageConfig.order,
      order_by: pageConfig.orderBy,
      search: debouncedSearch || undefined,
      org_chart_node_id: selectedRole ? Number(selectedRole) : undefined,
      status: selectedStatus || undefined,
      period: periodParam,
    });
  }, [
    currentTenant?.id,
    pageConfig,
    debouncedSearch,
    selectedRole,
    selectedStatus,
    startMonth,
    endMonth,
    selectedYear,
    getTemplates,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const roleOptions = orgChartNames.map(n => ({ label: n.name, value: String(n.id) }));

  const displayData = paginatedData ?? { content: [], totalElements: 0, totalPages: 0 };

  const columns: MainTableColumn[] = [
    {
      name: 'template_name',
      label: 'Nama Template',
      render: (_: unknown, row: IKpiTemplate) => row.template_name ?? row.name ?? '-',
    },
    {
      name: 'org_chart_node.name',
      label: 'Peran',
      render: (_: unknown, row: IKpiTemplate) => row.org_chart_node?.name ?? row.role?.name ?? '-',
    },
    {
      name: 'period',
      label: 'Periode',
      render: (_: unknown, row: IKpiTemplate) => formatPeriodLabel(row.period),
    },
    { name: 'total_kpi', label: 'Total KPI', render: (val: unknown) => String(val ?? '-') },
    {
      name: 'total_weight',
      label: 'Total Bobot',
      render: (val: unknown) => (val !== undefined && val !== null ? `${val}%` : '-'),
    },
    {
      name: 'status_kpi',
      label: 'Status',
      render: (_: unknown, row: IKpiTemplate) => <StatusBadge status={row.status_kpi ?? row.status} />,
    },
    {
      name: 'is_published',
      label: 'Publikasi',
      render: (val: unknown) => (
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            val ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
          }`}
        >
          {val ? 'Aktif' : 'Tidak Aktif'}
        </span>
      ),
    },
  ];

  const handleDelete = async (row: IKpiTemplate) => {
    if (!currentTenant?.id || !row.id) return;
    await deleteTemplate(currentTenant.id, row.id);
  };

  return (
    <section className="w-full">
      <CompanyHeader
        title="Template KPI Tim"
        description="Kelola template KPI untuk tim dalam organisasi."
        onCreate={isReadOnly ? undefined : () => navigate(`/${tenant_id}/kpi-template/create`)}
        createLabel="Tambah Template"
      />

      <div className="mx-5 border-b border-gray-200" />

      <div className="flex flex-col md:flex-row justify-start mt-5 px-5 gap-2">
        <SearchBar
          value={searchTerm}
          onChange={val => {
            setSearchTerm(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
          placeholder="Cari template..."
          className="w-full md:w-72"
        />
        <FilterSelect
          label="Peran"
          options={roleOptions}
          value={selectedRole}
          onChange={val => {
            setSelectedRole(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
        />
        <FilterSelect
          label="Status"
          options={STATUS_OPTIONS}
          value={selectedStatus}
          onChange={val => {
            setSelectedStatus(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
        />
        <FilterSelect
          label="Bulan Mulai"
          options={MONTHS_ID.map((m, i) => ({ label: m, value: String(i + 1).padStart(2, '0') }))}
          value={startMonth}
          onChange={val => {
            setStartMonth(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
          placeholder="Pilih bulan"
        />
        <FilterSelect
          label="Bulan Selesai"
          options={MONTHS_ID.map((m, i) => ({ label: m, value: String(i + 1).padStart(2, '0') }))}
          value={endMonth}
          onChange={val => {
            setEndMonth(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
          placeholder="Pilih bulan"
        />
        <FilterSelect
          label="Tahun"
          options={Array.from({ length: 11 }, (_, i) => {
            const year = (new Date().getFullYear() - 5 + i).toString();
            return { label: year, value: year };
          })}
          value={selectedYear}
          onChange={val => {
            setSelectedYear(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
          placeholder="Pilih tahun"
        />
        <ClearFilterButton
          onClear={() => {
            setSearchTerm('');
            setSelectedRole('');
            setSelectedStatus('');
            setStartMonth('');
            setEndMonth('');
            setSelectedYear('');
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
        />
      </div>

      <MainTable
        showHeader={false}
        isLoading={isLoading}
        data={{
          content: displayData.content as unknown as { [key: string]: unknown }[],
          totalElements: displayData.totalElements,
          totalPages: displayData.totalPages,
        }}
        columns={columns}
        pageConfig={pageConfig}
        setPageConfig={setPageConfig}
        onEdit={isReadOnly ? undefined : row => navigate(`/${tenant_id}/kpi-template/${(row as IKpiTemplate).id}/edit`)}
        showEditAction={!isReadOnly ? row => !(row.status_kpi === 'ACTIVE' && row.is_published) : undefined}
        onView={row => navigate(`/${tenant_id}/kpi-template/${(row as IKpiTemplate).id}/view`)}
        showViewAction={row => row.status_kpi === 'ACTIVE' && row.is_published}
        onDelete={isReadOnly ? undefined : handleDelete}
        showActions={!isReadOnly || true} // Show actions anyway to allow view for readonly users if desired, but requirements didn't explicitly mention this. Let's keep it based on requirement: "jika button view muncul maka button edit tidak muncul".
        useConfirmDelete
        sectionClassName="px-5"
      />
    </section>
  );
}
